/**
 * Custom error types for type-safe error handling across the application.
 * These replace the pattern of using `(error as any).code` with proper typed errors.
 */

/**
 * Base service error class for all API and service-related errors.
 * Provides structured error information that can be inspected without type casting.
 */
export class ServiceError extends Error {
  /** Error code for programmatic handling (e.g., 'RATE_LIMITED', 'NOT_FOUND') */
  public readonly code: string
  /** HTTP status code if applicable */
  public readonly statusCode?: number
  /** Additional context about the error */
  public readonly metadata?: Record<string, unknown>

  constructor(
    message: string,
    code: string,
    statusCode?: number,
    metadata?: Record<string, unknown>
  ) {
    super(message)
    this.name = 'ServiceError'
    this.code = code
    this.statusCode = statusCode
    this.metadata = metadata

    // Maintains proper stack trace for where error was thrown (V8 engines)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ServiceError)
    }
  }

  /**
   * Type guard to check if an unknown error is a ServiceError
   */
  static isServiceError(error: unknown): error is ServiceError {
    return error instanceof ServiceError
  }
}

/**
 * Error thrown when contact provider operations fail.
 * Includes cooldown information for rate limiting scenarios.
 */
export class ContactError extends ServiceError {
  /** ISO timestamp when the cooldown period ends (for rate limiting) */
  public readonly cooldownEndsAt?: string

  constructor(
    message: string,
    code: string,
    cooldownEndsAt?: string,
    statusCode?: number
  ) {
    super(message, code, statusCode, cooldownEndsAt ? { cooldownEndsAt } : undefined)
    this.name = 'ContactError'
    this.cooldownEndsAt = cooldownEndsAt

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ContactError)
    }
  }

  /**
   * Type guard to check if an unknown error is a ContactError
   */
  static isContactError(error: unknown): error is ContactError {
    return error instanceof ContactError
  }
}

/**
 * Error thrown when authentication fails or session is invalid.
 */
export class AuthError extends ServiceError {
  constructor(message: string, code: string = 'AUTH_ERROR', statusCode: number = 401) {
    super(message, code, statusCode)
    this.name = 'AuthError'

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AuthError)
    }
  }

  static isAuthError(error: unknown): error is AuthError {
    return error instanceof AuthError
  }
}

/**
 * Error thrown when network requests fail (timeout, connection refused, etc.)
 */
export class NetworkError extends ServiceError {
  /** Whether the request timed out */
  public readonly isTimeout: boolean

  constructor(message: string, isTimeout: boolean = false) {
    super(message, isTimeout ? 'TIMEOUT' : 'NETWORK_ERROR', undefined)
    this.name = 'NetworkError'
    this.isTimeout = isTimeout

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, NetworkError)
    }
  }

  static isNetworkError(error: unknown): error is NetworkError {
    return error instanceof NetworkError
  }
}

/**
 * Result type for functions that can fail.
 * Use this pattern instead of throwing errors when you want explicit error handling.
 *
 * @example
 * ```typescript
 * async function fetchData(): Promise<Result<Data>> {
 *   try {
 *     const data = await api.getData()
 *     return { success: true, data }
 *   } catch (e) {
 *     return { success: false, error: e instanceof Error ? e : new Error(String(e)) }
 *   }
 * }
 *
 * // Usage:
 * const result = await fetchData()
 * if (result.success) {
 *   console.log(result.data)
 * } else {
 *   console.error(result.error.message)
 * }
 * ```
 */
export type Result<T, E extends Error = Error> =
  | { success: true; data: T }
  | { success: false; error: E }

/**
 * Helper function to create a successful result
 */
export function ok<T>(data: T): Result<T, never> {
  return { success: true, data }
}

/**
 * Helper function to create a failed result
 */
export function err<E extends Error>(error: E): Result<never, E> {
  return { success: false, error }
}

/**
 * Extract the data type from a Result type
 */
export type ResultData<R> = R extends Result<infer T, Error> ? T : never

/**
 * Extract the error type from a Result type
 */
export type ResultError<R> = R extends Result<unknown, infer E> ? E : never
